import {useEffect, useRef} from 'react'
import {TID} from '@atproto/common-web'
import {overwriteSavedFeeds} from '@bsky/sdk'
import {useQueryClient} from '@tanstack/react-query'

import {
  CULTURE_FEED_URI,
  CULTURE_SAVED_FEED,
  DISCOVER_FEED_URI,
} from '#/lib/constants'
import {logger} from '#/logger'
import * as persisted from '#/state/persisted'
import {
  preferencesQueryKey,
  usePreferencesQuery,
} from '#/state/queries/preferences'
import {usePdsClient, useSession} from '#/state/session'

function markMigrated(did: string) {
  const migrated = persisted.get('migratedCultureFeedDids') ?? []
  if (migrated.includes(did)) return
  void persisted.write('migratedCultureFeedDids', [...migrated, did])
}

/**
 * One-shot per-DID migration: pin The Culture feed for existing accounts that
 * do not already have it saved. Mount below QueryProvider (same place as
 * BetaUserStorageSync).
 */
export function CultureFeedMigration() {
  const {currentAccount} = useSession()
  const pdsClient = usePdsClient()
  const queryClient = useQueryClient()
  const {data: preferences} = usePreferencesQuery()
  const inFlight = useRef(false)

  useEffect(() => {
    const did = currentAccount?.did
    if (!did || !preferences) return

    const migrated = persisted.get('migratedCultureFeedDids') ?? []
    if (migrated.includes(did)) return

    if (preferences.savedFeeds.some(f => f.value === CULTURE_FEED_URI)) {
      markMigrated(did)
      return
    }

    if (inFlight.current) return
    inFlight.current = true

    const migrate = async () => {
      try {
        const culture = {
          ...CULTURE_SAVED_FEED,
          id: TID.nextStr(),
        }
        const feeds = preferences.savedFeeds
        const discoverIdx = feeds.findIndex(f => f.value === DISCOVER_FEED_URI)
        const next =
          discoverIdx >= 0
            ? [
                ...feeds.slice(0, discoverIdx + 1),
                culture,
                ...feeds.slice(discoverIdx + 1),
              ]
            : [culture, ...feeds]

        await pdsClient.call(overwriteSavedFeeds, next)
        await queryClient.invalidateQueries({
          queryKey: preferencesQueryKey,
        })
        markMigrated(did)
      } catch (e) {
        logger.error('culture feed migration failed', {safeMessage: e})
      } finally {
        inFlight.current = false
      }
    }

    void migrate()
  }, [pdsClient, currentAccount?.did, preferences, queryClient])

  return null
}
